import { PersonalInformationConsent, Report, Seasons } from 'store/ducks'
import {
  injuryEntryHasData,
  shouldUseOldInjuryFormat
} from '../../../utils/injury-format-utils'

export const ValidateReportSections = (report: Report): Report => {
  // IMPORTANT: do not mutate the input report (Formik/Redux invariant)
  // Deep clone to safely compute flags without side effects
  const next: Report = JSON.parse(JSON.stringify(report)) as Report

  const {
    personalInformation,
    season,
    sequenceOfEvents,
    transport,
    treatedBy,
    investigationAndMonitoring,
    state,
    timestamps,
    injury,
    checklist
  } = next

  // validate personalInformation

  const {
    firstName,
    lastName,
    address,
    phoneNumber,
    gender,
    birthDate,
    place,
    postalCode,
    country,
    personalInformationConsent,
    skills,
    equipments,
    nationality
  } = personalInformation

  let showPersonInfoForm = false

  switch (personalInformationConsent) {
    case PersonalInformationConsent.PERMISSION_CONSENTED:
    case PersonalInformationConsent.CONSENT_TO_WRITTEN_REPORT:
      showPersonInfoForm = true
      break
    case PersonalInformationConsent.NOT_RESPONSIVE_CANT_GET_CONSENT:
    case PersonalInformationConsent.PATIENT_HAS_NOT_GIVEN_CONSENT:
      showPersonInfoForm = false
      break
  }

  if (showPersonInfoForm) {
    if (
      firstName &&
      lastName &&
      address &&
      phoneNumber &&
      gender &&
      birthDate &&
      place &&
      postalCode &&
      country &&
      nationality
    ) {
      next.personalInformation.personalInfoFormCompleted = true
    }
  } else {
    next.personalInformation.personalInfoFormCompleted = true
  }

  if (personalInformation.firstName && personalInformation.lastName) {
    next.patientName =
      personalInformation.firstName.toLowerCase() +
      ' ' +
      personalInformation.lastName.toLowerCase()
  }

  if (skills && skills.level) {
    next.personalInformation.skillsCompleted = true
  }

  if (equipments) {
    if (season === Seasons.WINTER) {
      if (equipments.skiEquipment && equipments.skiEquipment.type) {
        next.personalInformation.equipmentsCompleted = true
      }
    }

    if (season === Seasons.SUMMER) {
      if (equipments.bikeEquipment && equipments.bikeEquipment.type) {
        next.personalInformation.equipmentsCompleted = true
      }
    }
  }

  if (
    next.personalInformation.personalInformationConsent ===
      PersonalInformationConsent.CONSENT_TO_WRITTEN_REPORT ||
    next.personalInformation.personalInformationConsent ===
      PersonalInformationConsent.PERMISSION_CONSENTED
  ) {
    next.personalInfoCompleted =
      (next.personalInformation.personalInfoFormCompleted &&
        next.personalInformation.skillsCompleted &&
        next.personalInformation.equipmentsCompleted) ||
      false
  }

  if (
    next.personalInformation.personalInformationConsent ===
      PersonalInformationConsent.NOT_RESPONSIVE_CANT_GET_CONSENT ||
    next.personalInformation.personalInformationConsent ===
      PersonalInformationConsent.PATIENT_HAS_NOT_GIVEN_CONSENT
  ) {
    next.personalInfoCompleted =
      (next.personalInformation.skillsCompleted &&
        next.personalInformation.equipmentsCompleted) ||
      false
  }

  // validate personalInformation

  // Validate injury section with backward compatibility
  if (shouldUseOldInjuryFormat(next)) {
    const { assumedInjury, injuredBodyPart } = injury || {}

    if (assumedInjury && next.injury) {
      next.injury!.assumedInjuryCompleted =
        assumedInjury.acuteSickness ||
        assumedInjury.concussion ||
        assumedInjury.cutsWounds ||
        assumedInjury.dead ||
        assumedInjury.dislocated ||
        assumedInjury.fracture ||
        assumedInjury.impactShock ||
        assumedInjury.other ||
        assumedInjury.sprain ||
        false
    }

    if (injuredBodyPart) {
      if (injuredBodyPart && next.injury) {
        next.injury!.injuryBodyPartCompleted =
          injuredBodyPart.ankleFoot ||
          injuredBodyPart.arm ||
          injuredBodyPart.back ||
          injuredBodyPart.breast ||
          injuredBodyPart.buttock ||
          injuredBodyPart.calf ||
          injuredBodyPart.collarbone ||
          injuredBodyPart.finger ||
          injuredBodyPart.hand ||
          injuredBodyPart.head ||
          injuredBodyPart.hip ||
          injuredBodyPart.knee ||
          false
      }
    }

    if (
      next &&
      (next.injury?.assumedInjuryCompleted ||
        next.injury?.injuryBodyPartCompleted)
    ) {
      next.injuryCompleted = true
    }
  } else {
    // Use new validation for injuryDataV2 for reports after Sept 1, 2025 or when injuryDataV2 exists
    const { injuryDataV2 } = next
    if (injuryDataV2 && injuryDataV2.assumedInjuries?.length > 0) {
      const hasAnyInjuryData =
        injuryDataV2.assumedInjuries.some(injuryEntryHasData)
      if (hasAnyInjuryData) {
        next.injuryCompleted = true
      }
    }
  }

  // sequence of events

  if (sequenceOfEvents) {
    if (
      next.sequenceOfEvents &&
      sequenceOfEvents.injuryLocation &&
      (sequenceOfEvents.injuryLocation.adaptedPath ||
        sequenceOfEvents.injuryLocation.closedDownSpeed ||
        sequenceOfEvents.injuryLocation.jump ||
        sequenceOfEvents.injuryLocation.naturalPath ||
        sequenceOfEvents.injuryLocation.other ||
        sequenceOfEvents.injuryLocation.outsidePreparedDownSpeed ||
        sequenceOfEvents.injuryLocation.preparedDownSpeed ||
        sequenceOfEvents.injuryLocation.pumptrack ||
        sequenceOfEvents.injuryLocation.roadGravelRoad ||
        sequenceOfEvents.injuryLocation.terrainParkAtElement ||
        sequenceOfEvents.injuryLocation.tractionElement ||
        false)
    ) {
      next.sequenceOfEvents!.injuryLocationCompleted = true
    }

    if (
      next.sequenceOfEvents &&
      sequenceOfEvents &&
      sequenceOfEvents.witnesses &&
      sequenceOfEvents?.witnesses?.length > 0
    ) {
      next.sequenceOfEvents!.witnessCompleted = true
    }

    if (
      next.sequenceOfEvents &&
      (sequenceOfEvents.eventExplanation ||
        sequenceOfEvents.eventExplanationLocation ||
        sequenceOfEvents.fallYourself ||
        sequenceOfEvents.hitByAnotherPerson ||
        sequenceOfEvents.runOverAnotherPerson ||
        sequenceOfEvents.collisionWithObject ||
        sequenceOfEvents.lostControl ||
        sequenceOfEvents.drivenOutOfBerm ||
        sequenceOfEvents.crashObjectOnTrail ||
        sequenceOfEvents.lostBalanceAfterJump ||
        sequenceOfEvents.hitByAnotherCyclist)
    ) {
      next.sequenceOfEvents!.eventExplanationCompleted = true
    }

    if (
      next.sequenceOfEvents?.eventExplanationCompleted ||
      next.sequenceOfEvents?.injuryLocationCompleted
    ) {
      next.sequenceOfEventsCompleted = true
    }
  }

  // timestamps

  if (timestamps) {
    const {
      notificationOfInjuryTime,
      skiPatrolAtInjurySite,
      pathPatrolAtInjurySite,
      departureFromInjurySiteTime,
      arrivedAtInjuryRoomTime,
      amkWarningTime,
      amisNumberTime,
      patientDeliveredTime,
      policeWarnedTime
    } = timestamps
    if (
      notificationOfInjuryTime &&
      (skiPatrolAtInjurySite || pathPatrolAtInjurySite) &&
      departureFromInjurySiteTime &&
      arrivedAtInjuryRoomTime &&
      amkWarningTime &&
      amisNumberTime &&
      patientDeliveredTime &&
      policeWarnedTime
    ) {
      next.timeStampsCompleted = true
    }
  }

  // state
  if (state) {
    next.stateCompleted = true
  }

  // transport

  if (transport) {
    let transportAtResort = false
    let furtherTransport = false

    if (
      transport.transportAtResort &&
      (transport.transportAtResort.atv ||
        transport.transportAtResort.own ||
        transport.transportAtResort.pulk ||
        transport.transportAtResort.snowScooter ||
        transport.transportAtResort.scooterPulk ||
        transport.transportAtResort.atvUtv ||
        transport.transportAtResort.walkingCyclingYourself ||
        transport.transportAtResort.stretcher ||
        transport.transportAtResort.fireAndRescue ||
        false)
    ) {
      transportAtResort = true
    }

    if (
      transport.furtherTransport &&
      (transport.furtherTransport.airAmbulance ||
        transport.furtherTransport.ambulance ||
        transport.furtherTransport.privateCar ||
        transport.furtherTransport.stayingAtSkiResort ||
        transport.furtherTransport.taxi ||
        transport.furtherTransport.stiPTrspEmergencyRoom ||
        false)
    ) {
      furtherTransport = true
    }

    if (transportAtResort && furtherTransport) {
      next.transportationCompleted = true
    }
  }

  // investigation and monitoring

  if (
    investigationAndMonitoring &&
    investigationAndMonitoring.monitoring.length > 0
  ) {
    next.investigationAndMonitoringCompleted = true
  }

  // TREATED BY

  if (
    treatedBy &&
    (treatedBy.auxiliaryCross ||
      treatedBy.contactDoctorIfConditionsWorsens ||
      treatedBy.doctorHospital ||
      treatedBy.emergencyRoomAmk ||
      treatedBy.otherEmployee ||
      treatedBy.skiPatrol ||
      treatedBy.seeDoctorIfConditionsWorsens ||
      treatedBy.emergencyRoom ||
      treatedBy.ambulanceOnSite ||
      treatedBy.doctorOnSite ||
      false)
  ) {
    next.treatedByCompleted = true
  }

  if (
    checklist &&
    (checklist.clearAirways ||
      checklist.hlr ||
      checklist.oxygen ||
      checklist.bandaging ||
      checklist.reportFracture ||
      checklist.splint ||
      checklist.stretchSplint ||
      checklist.neckCollar ||
      checklist.vacuumMattress ||
      checklist.price ||
      checklist.cryo ||
      checklist.backboard ||
      checklist.woundCleanser ||
      false)
  ) {
    next.checklistCompleted = true
  }
  return next
}
